import { useEffect, useRef, useState } from 'react';

import { IconButton, Paper, Stack, Tooltip } from '@mui/material';

import {
  useBoardEditingStore,
  useWallStore,
  useWorkspaceUiStore,
  type WorkspaceTool,
} from '../../../store';
import { getPanelColor, getPanelTitleColor } from '../theme/wallThemeStyles';
import { CardToolDialog } from './CardToolDialog';
import { ShapeToolDialog } from './ShapeToolDialog';
import { TextToolDialog } from './TextToolDialog';

type PageTool = WorkspaceTool | 'drawing' | 'image';
export interface PageToolAvailability {
  allowCardCreation: boolean;
  allowClustering: boolean;
  allowDrawingTools: boolean;
}

const tools: Array<{ tool: PageTool; label: string; icon: JSX.Element }> = [
  {
    tool: 'pointer',
    label: 'Pointer',
    icon: (
      <svg
        style={{ marginBottom: -9 }}
        viewBox="0 0 333.33 416.66249999999997"
        height="26"
        fillRule="evenodd"
        clipRule="evenodd"
      >
        <g>
          <path
            style={{
              fill: 'black',
              fillRule: 'nonzero',
            }}
            d="M286.2 286.2l15.45 -15.45 -99.63 -99.63c-3.37,-3.37 -3.37,-8.84 0,-12.21 0.6,-0.6 1.28,-1.1 1.99,-1.48l73.63 -45.85 -244.87 -78.8 78.8 244.87 46.12 -74.07c2.52,-4.04 7.83,-5.28 11.87,-2.76 0.57,0.35 1.09,0.77 1.54,1.22l0.01 -0.01 99.63 99.63 15.45 -15.45zm33.77 -9.35l-43.12 43.12c-3.37,3.37 -8.84,3.37 -12.21,0l-98.06 -98.06 -50.19 80.61 -0.01 -0c-1.04,1.67 -2.65,3 -4.68,3.65 -4.53,1.45 -9.37,-1.04 -10.83,-5.57l-89.63 -278.52c-0.54,-1.65 -0.57,-3.49 -0,-5.27 1.45,-4.52 6.3,-7.02 10.83,-5.57l278.11 89.5c2.04,0.55 3.89,1.84 5.1,3.78 2.52,4.04 1.28,9.36 -2.77,11.87l-80.61 50.19 98.06 98.06c3.37,3.37 3.37,8.84 0,12.21z"
          ></path>
        </g>
      </svg>
    ),
  },
  {
    tool: 'card',
    label: 'Card',
    icon: (
      <svg viewBox="0 0 32 40" height="28" style={{ marginBottom: -8 }}>
        <g data-name="65-note">
          <path d="M30.9342,21.3257a.984.984,0,0,0,.0533-.2639C30.989,21.04,31,21.0219,31,21V2a1,1,0,0,0-1-1H2A1,1,0,0,0,1,2V30a1,1,0,0,0,1,1H21a1.01,1.01,0,0,0,.7077-.2936l8.9985-8.9985.0034-.0043a.9883.9883,0,0,0,.191-.2833A.9632.9632,0,0,0,30.9342,21.3257ZM20,21v8H3V3H29V20H21A1,1,0,0,0,20,21Zm2,6.5859V22h5.5859Z"></path>
        </g>
      </svg>
    ),
  },
  {
    tool: 'image',
    label: 'Image',
    icon: (
      <svg height="32" viewBox="0 0 40 40" style={{ marginBottom: -8 }}>
        <g transform="translate(-56 -453)">
          <g transform="translate(59.315 457)">
            <rect
              fill="none"
              stroke="#0f2a39"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              width="28"
              height="28"
              rx="2"
              transform="translate(2.685 3)"
            />
            <circle
              fill="none"
              stroke="#0f2a39"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              cx="2.5"
              cy="2.5"
              r="2.5"
              transform="translate(8.685 9)"
            />
            <path
              fill="none"
              stroke="#0f2a39"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M29.608,17.69,21.918,10,5,26.918"
              transform="translate(1.076 3.766)"
            />
          </g>
          <rect fill="none" width="40" height="40" transform="translate(56 453)" />
        </g>
      </svg>
    ),
  },
  {
    tool: 'cluster',
    label: 'Group',
    icon: (
      <svg
        height="30"
        viewBox="0 0 24 24"
        style={{ marginBottom: -4 }}
        fill="none"
        stroke="#000000"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.3"
      >
        <path d="m12 2.8 8 6-8 6-8-6z"></path>
        <path d="m4 13.2 8 6 8-6"></path>
      </svg>
    ),
  },
  {
    tool: 'text',
    label: 'Text',
    icon: (
      <svg height="30" viewBox="0 0 40 40" style={{ marginTop: -3, marginBottom: -8 }}>
        <g transform="translate(-56 -536)">
          <rect
            style={{
              fill: 'none',
            }}
            width="40"
            height="40"
            transform="translate(56 536)"
          ></rect>
          <path
            style={{
              fill: 'none',
              stroke: '#0f2a39',
              strokeWidth: 2,
            }}
            d="M80-604h24"
            transform="translate(-16 1148)"
          ></path>
          <path
            style={{
              fill: 'none',
              stroke: '#0f2a39',
              strokeWidth: 2,
            }}
            d="M92-604v24"
            transform="translate(-16 1148)"
          ></path>
        </g>
      </svg>
    ),
  },
  {
    tool: 'drawing',
    label: 'Line / Shape',
    icon: (
      <svg height="32" viewBox="0 0 64 80" style={{ marginBottom: -10 }}>
        <path d="M60.706,22.747H47.483C46.708,10.833,36.772,1.375,24.666,1.375c-12.612,0-22.872,10.26-22.872,22.872  c0,11.683,8.979,21.554,20.534,22.733v14.145c0,0.828,0.671,1.5,1.5,1.5h36.878c0.828,0,1.5-0.672,1.5-1.5V24.247  C62.206,23.418,61.534,22.747,60.706,22.747z M4.794,24.247c0-10.958,8.915-19.872,19.872-19.872  c10.451,0,19.024,8.115,19.796,18.372H23.828c-0.829,0-1.5,0.671-1.5,1.5v19.714C12.438,42.795,4.794,34.295,4.794,24.247z   M59.206,59.625H25.328V25.747h33.878V59.625z"></path>
      </svg>
    ),
  },
];

interface PageToolsProps {
  allowCardCreation: boolean;
  allowClustering: boolean;
  allowDrawingTools: boolean;
  layoutStyle?: string;
}

export function PageTools({
  allowCardCreation,
  allowClustering,
  allowDrawingTools,
  layoutStyle,
}: PageToolsProps) {
  const [openToolMenu, setOpenToolMenu] = useState<PageTool | null>(null);
  const lastDrawingToolRef = useRef<Extract<WorkspaceTool, 'line' | 'shape'>>('shape');
  const selectedTool = useWorkspaceUiStore((state) => state.selectedTool);
  const setSelectedTool = useWorkspaceUiStore((state) => state.setSelectedTool);
  const startAddImage = useBoardEditingStore((state) => state.startAddImage);
  const theme = useWallStore((state) => state.theme);
  const panelColor = getPanelColor(theme);
  const panelTitleColor = getPanelTitleColor(theme);
  const visibleTools = tools.filter((item) =>
    isToolAllowed(item.tool, { allowCardCreation, allowClustering, allowDrawingTools })
  );

  const selectTool = (tool: PageTool) => {
    if (tool === 'image') {
      startAddImage();
      setOpenToolMenu(null);
      return;
    }

    const nextTool =
      tool === 'drawing'
        ? isDrawingTool(selectedTool)
          ? selectedTool
          : lastDrawingToolRef.current
        : tool;

    setSelectedTool(nextTool);
    setOpenToolMenu((openTool) =>
      tool === 'drawing' || tool === 'text' || tool === 'card'
        ? selectedTool !== nextTool || openTool !== tool
          ? tool
          : null
        : null
    );
  };

  useEffect(() => {
    if (isDrawingTool(selectedTool)) {
      lastDrawingToolRef.current = selectedTool;
    }

    if (!isDrawingTool(selectedTool) && selectedTool !== 'text' && selectedTool !== 'card') {
      setOpenToolMenu(null);
    }
  }, [selectedTool]);

  useEffect(() => {
    if (isToolAllowed(selectedTool, { allowCardCreation, allowClustering, allowDrawingTools })) {
      return;
    }

    setSelectedTool('pointer');
    setOpenToolMenu(null);
  }, [allowCardCreation, allowClustering, allowDrawingTools, selectedTool, setSelectedTool]);

  useEffect(() => {
    if (
      openToolMenu &&
      !isToolAllowed(openToolMenu, { allowCardCreation, allowClustering, allowDrawingTools })
    ) {
      setOpenToolMenu(null);
    }
  }, [allowCardCreation, allowClustering, allowDrawingTools, openToolMenu]);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '8px',
        bgcolor: panelColor,
        boxShadow: '0px 3px 6px #00000029',
        left: { xs: 10, sm: 20 },
        p: '5px 0',
        position: 'fixed',
        top: {
          xs: layoutStyle === 'open' ? 70 : 'auto',
          sm: '50%',
        },
        bottom: {
          xs: layoutStyle === 'open' ? 'auto' : 10,
          sm: 'auto',
        },
        transform: { xs: 'none', sm: 'translateY(-50%)' },
        width: 50,
        zIndex: 1000,
      }}
    >
      <Stack>
        {visibleTools.map((item) => (
          <Tooltip key={item.tool} title={item.label} placement="right">
            <IconButton
              color={isSelectedPageTool(item.tool, selectedTool) ? 'primary' : 'default'}
              onClick={() => selectTool(item.tool)}
              aria-label={item.label}
              sx={{
                borderRadius: 0,
                color: panelTitleColor,
                height: 50,
                width: '100%',
                backgroundColor: isSelectedPageTool(item.tool, selectedTool)
                  ? 'rgba(255, 255, 255, 0.25)'
                  : 'transparent',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' },
              }}
            >
              {item.icon}
            </IconButton>
          </Tooltip>
        ))}
        {openToolMenu === 'card' && selectedTool === 'card' && (
          <CardToolDialog layoutStyle={layoutStyle} onColorSelect={() => setOpenToolMenu(null)} />
        )}
        {openToolMenu === 'text' && selectedTool === 'text' && (
          <TextToolDialog layoutStyle={layoutStyle} />
        )}
        {openToolMenu === 'drawing' && isDrawingTool(selectedTool) && (
          <ShapeToolDialog layoutStyle={layoutStyle} />
        )}
      </Stack>
    </Paper>
  );
}

function isDrawingTool(tool: WorkspaceTool): tool is Extract<WorkspaceTool, 'line' | 'shape'> {
  return tool === 'shape' || tool === 'line';
}

function isSelectedPageTool(tool: PageTool, selectedTool: WorkspaceTool) {
  if (tool === 'image') {
    return false;
  }

  return tool === 'drawing' ? isDrawingTool(selectedTool) : selectedTool === tool;
}

function isToolAllowed(
  tool: PageTool,
  { allowCardCreation, allowClustering, allowDrawingTools }: PageToolAvailability
) {
  if (tool === 'pointer' || tool === 'hand') {
    return true;
  }

  if (tool === 'card' || tool === 'image') {
    return allowCardCreation;
  }

  if (tool === 'cluster') {
    return allowCardCreation && allowClustering;
  }

  if (tool === 'drawing' || tool === 'line' || tool === 'shape' || tool === 'text') {
    return allowDrawingTools;
  }

  return true;
}

export function hasEnabledPageTool(availability: PageToolAvailability) {
  return tools.some((item) => item.tool !== 'pointer' && isToolAllowed(item.tool, availability));
}
